import {
  type CardBrand,
  type CardOptions,
  type CardFieldNamesValues,
  type TokenError,
} from '@square/web-payments-sdk-types';
import { CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import varsStyle from '../../styles/vars.scss';
import { type DeviceType } from '../../types/DeviceType';
import { FieldName } from '../../types/CardFieldName';
import { type Theme } from '../../types/Theme';
import { TokenizationErrorCodes } from '../../types/TokenizationErrorCodes';
import { type ProviderError } from '../../utils/messages/types';
import { type SqPaymentCardBrand, type SqPaymentFormNonceGenerationError } from './types';

export const squareToInternalCardNetworkMap: Record<SqPaymentCardBrand, CreditCardNetwork> = {
  americanExpress: CreditCardNetwork.Amex,
  masterCard: CreditCardNetwork.MasterCard,
  discover: CreditCardNetwork.Discover,
  discoverDiners: CreditCardNetwork.Diners,
  JCB: CreditCardNetwork.JCB,
  unionPay: CreditCardNetwork.UnionPay,
  visa: CreditCardNetwork.Visa,
  unknown: null,
};

export const squareTokenizeToInternalCardNetworkMap: Record<CardBrand, CreditCardNetwork> = {
  AMERICAN_EXPRESS: CreditCardNetwork.Amex,
  MASTERCARD: CreditCardNetwork.MasterCard,
  DISCOVER: CreditCardNetwork.Discover,
  DISCOVER_DINERS: CreditCardNetwork.Diners,
  JCB: CreditCardNetwork.JCB,
  CHINA_UNIONPAY: CreditCardNetwork.UnionPay,
  VISA: CreditCardNetwork.Visa,
  OTHER_BRAND: null,
};

export const internalToSquareFieldNamesMap: Partial<Record<FieldName, CardFieldNamesValues>> = {
  [FieldName.Number]: 'cardNumber',
  [FieldName.CVV]: 'cvv',
  [FieldName.ExpireDate]: 'expirationDate',
  [FieldName.PostalCode]: 'postalCode',
};

export const squareToInternalFieldNamesMap: Partial<Record<CardFieldNamesValues, FieldName>> = Object.entries(
  internalToSquareFieldNamesMap,
).reduce<Partial<Record<CardFieldNamesValues, FieldName>>>((acc, [internalName, squareName]) => {
  acc[squareName] = internalName as FieldName;
  return acc;
}, {});

const standardThemeStyles: CardOptions['style'] = {
  '.input-container': {
    borderRadius: varsStyle.inputBorderRadius,
    borderColor: varsStyle.inputBorderColor,
    borderWidth: '0',
  },
  '.input-container.is-focus': {
    borderColor: varsStyle.focusedInputBorderColor,
    borderWidth: '0',
  },
  '.input-container.is-error': {
    borderColor: varsStyle.errorColor,
    borderWidth: '0',
  },
  input: {
    backgroundColor: varsStyle.backgroundColorWhite,
    color: varsStyle.fontColor,
  },
  'input.is-focus': {
    color: varsStyle.fontColor,
  },
  'input.is-error': {
    color: varsStyle.fontColor,
  },
  'input::placeholder': {
    color: varsStyle.placeholderColor,
  },
  'input.is-error::placeholder': {
    color: varsStyle.placeholderColor,
  },
};

const minimalThemeStyles: CardOptions['style'] = {
  ...standardThemeStyles,
};

const wsrThemeStyles: CardOptions['style'] = {
  ...standardThemeStyles,

  '.input-container': {
    ...standardThemeStyles['.input-container'],
    borderColor: varsStyle.wsrInputBorderColor,
    borderRadius: varsStyle.wsrInputBorderRadius,
  },
  '.input-container.is-focus': {
    ...standardThemeStyles['.input-container.is-focus'],
    borderColor: varsStyle.wsrFocusedInputBorderColor,
  },
  '.input-container.is-error': {
    ...standardThemeStyles['.input-container.is-error'],
    borderColor: varsStyle.wsrErrorColor,
  },
  input: {
    color: varsStyle.wsrFontColor,
    backgroundColor: varsStyle.backgroundColorWhite,
  },
  'input.is-focus': {
    color: varsStyle.wsrFontColor,
  },
  'input.is-error': {
    color: varsStyle.wsrFontColor,
  },
};

const themeMap: Record<Theme, CardOptions['style']> = {
  standard: standardThemeStyles,
  minimal: minimalThemeStyles,
  'wsr-like': wsrThemeStyles,
};

export const getSquareWebSDKInputStyles = (deviceType: DeviceType, theme: Theme): CardOptions['style'] => {
  // Customize the Card Entry Form - https://developer.squareup.com/docs/web-payments/customize-styles

  let styles: CardOptions['style'] = themeMap[theme];

  if (theme === 'minimal' || theme === 'standard') {
    styles = {
      ...styles,
      '.input-container': {
        ...styles['.input-container'],
        ...(deviceType === 'mobile' && {
          borderColor: varsStyle.mobileInputBorderColor,
        }),
      },
    };
  }

  return styles;
};

const isSquareTokenErrorDetails = (error: unknown): error is Extract<TokenError, { type: string }> =>
  typeof error === 'object' &&
  error !== null &&
  'type' in error &&
  typeof (error as { type: unknown }).type === 'string';

export const mapSquareTokenizeErrorToErrorCode = (error: unknown): number => {
  if (isSquareTokenErrorDetails(error) && error.type && error.field) {
    switch (error.type as SqPaymentFormNonceGenerationError) {
      case 'VALIDATION_ERROR':
        switch (error.field as CardFieldNamesValues) {
          case 'cardNumber':
            return TokenizationErrorCodes.InvalidCardNumber;
          case 'expirationDate':
            return TokenizationErrorCodes.InvalidExpirationDate;
          case 'cvv':
            return TokenizationErrorCodes.InvalidCVV;
          case 'postalCode':
            return TokenizationErrorCodes.InvalidZipCode;
          default:
            return TokenizationErrorCodes.GeneralValidationError;
        }
      case 'UNSUPPORTED_CARD_BRAND':
        return TokenizationErrorCodes.CardTypeIsNotSupported; // "This payment method does not work with your card. Try a different card or another payment method."
      case 'UNAUTHORIZED':
        return TokenizationErrorCodes.ThreeDSecureFailed; // "Your card was not authenticated. Review your details and try again, try a different card or use a different payment method."
      default:
    }
  }

  return TokenizationErrorCodes.GeneralTokenizationError;
};

export const extractSquareTokenizeErrorDetails = (error: unknown): ProviderError | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const type = isSquareTokenErrorDetails(error) ? error.type : (error as Error).name;
  const message = (error as Error).message;

  if (type === undefined && message === undefined) {
    return undefined;
  }

  return { type, message };
};
